import * as React from "react";
import { flattenSkillsByPriority } from "../catalog";
import {
  ensureSkillsCatalog,
  getCachedSkillsCatalog,
  getSkillsCatalogStoreVersion,
  refreshSkillsCatalog,
  subscribeToSkillsCatalog,
} from "../catalogStore";
import type { SkillsCatalog, SkillOrigin, SkillSummary, SkillViewStatus } from "../types";

export interface UseSkillsCatalogOptions {
  /** When true, taskpane focus/visibility refetches even inside the cooldown. */
  forceRefreshOnFocus?: boolean;
}

export interface UseSkillsCatalogResult {
  status: SkillViewStatus;
  catalog: SkillsCatalog | undefined;
  skills: Array<SkillSummary & { origin: SkillOrigin }>;
  error: Error | null;
}

/**
 * Thin React adapter around the user-scoped shared catalog store. The caller
 * supplies the already-resolved feature decision; this hook never reads a host
 * or feature-flag service.
 */
export function useSkillsCatalog(
  enabled: boolean,
  options?: UseSkillsCatalogOptions
): UseSkillsCatalogResult {
  const forceRefreshOnFocus = Boolean(options?.forceRefreshOnFocus);
  const storeVersion = React.useSyncExternalStore(
    subscribeToSkillsCatalog,
    getSkillsCatalogStoreVersion,
    () => 0
  );
  const [requestState, setRequestState] = React.useState<{
    status: SkillViewStatus;
    error: Error | null;
  }>({ status: "idle", error: null });
  const catalog = getCachedSkillsCatalog();

  React.useEffect(() => {
    if (!enabled) {
      setRequestState({ status: "idle", error: null });
      return undefined;
    }
    if (catalog) {
      setRequestState({ status: "ready", error: null });
      return undefined;
    }

    let cancelled = false;
    setRequestState({ status: "loading", error: null });
    void ensureSkillsCatalog()
      .then(() => {
        if (!cancelled) {
          setRequestState({ status: "ready", error: null });
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setRequestState({
            status: "error",
            error: error instanceof Error ? error : new Error("Unable to load Skills."),
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [catalog, enabled, storeVersion]);

  React.useEffect(() => {
    if (!enabled) return undefined;

    const refreshIfVisible = () => {
      if (document.visibilityState !== "visible") return;
      void refreshSkillsCatalog({ force: forceRefreshOnFocus }).catch(() => undefined);
    };

    window.addEventListener("focus", refreshIfVisible);
    document.addEventListener("visibilitychange", refreshIfVisible);
    return () => {
      window.removeEventListener("focus", refreshIfVisible);
      document.removeEventListener("visibilitychange", refreshIfVisible);
    };
  }, [enabled, forceRefreshOnFocus]);

  return {
    status: catalog && enabled ? "ready" : requestState.status,
    catalog: enabled ? catalog : undefined,
    skills: enabled && catalog ? flattenSkillsByPriority(catalog.personal, catalog.directory) : [],
    error: enabled ? requestState.error : null,
  };
}
